/* ==========================================================================
   Guide · Field Manual — behaviour
   Ported from docs/Guide-Page-Redesign.dc.html option 2a.

   Everything the page body needs to talk to this script is expressed with
   classes and hidden <span class="gfm-cfg"> text nodes, never data-*
   attributes: the Shopify admin rich-text editor strips data-* when someone
   edits a page by hand, and these guides are hand-edited.

   Config comes from window.__SKIRMSHOP_GUIDE_CONFIG (see
   snippets/guide-fieldmanual.liquid).
   ========================================================================== */
(function () {
  'use strict';

  var CONFIG = window.__SKIRMSHOP_GUIDE_CONFIG || {};
  var ROOT_PATH = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Field Manual pages wrap everything in one .gfm, so scoping to it is right for
     the slots that belong to a layout. The assistant is different: a legacy guide
     drops a .gfm-ask-slot between its own <h2> sections, each in its own small
     .gfm wrapper just to pick up the --gfm-* custom properties. Scoped to the
     FIRST .gfm, only the first assistant on such a page was ever built and the
     rest rendered their suggested questions as loose text. */
  var root = document.querySelector('.gfm') || document;

  /* ---------------------------------------------------------------- helpers */

  function cfgOf(el) {
    var span = el && el.querySelector(':scope > .gfm-cfg');
    return span ? span.textContent.trim() : '';
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Minimal markdown: bold, bullet lists, paragraphs. Same subset the chat
     widget already renders, so answers look identical in both surfaces. */
  function formatAnswer(raw) {
    var text = String(raw || '')
      .replace(/\[TOOL:[^\]]*\]/g, '')
      .trim();
    if (!text) return '';
    var e = escapeHtml(text).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    var lines = e.split('\n');
    var html = [];
    var inList = false;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].trim();
      var bullet = line.match(/^[-•*]\s+(.+)/) || line.match(/^\d+[.)]\s+(.+)/);
      if (bullet) {
        if (!inList) {
          html.push('<ul>');
          inList = true;
        }
        html.push('<li>' + bullet[1] + '</li>');
      } else {
        if (inList) {
          html.push('</ul>');
          inList = false;
        }
        if (line) html.push('<p>' + line + '</p>');
      }
    }
    if (inList) html.push('</ul>');
    return html.join('');
  }

  function t(key, fallback) {
    var dict = CONFIG.strings || {};
    return dict[key] || fallback;
  }

  /* ------------------------------------------------------- 1. section rail */

  function initRail() {
    var rail = root.querySelector('.gfm-rail');
    if (!rail) return;

    var links = Array.prototype.slice.call(rail.querySelectorAll('a[href^="#"]'));
    if (!links.length) return;

    var byId = {};
    var targets = [];
    links.forEach(function (link) {
      var id = link.getAttribute('href').slice(1);
      var section = id && document.getElementById(id);
      if (!section) return;
      byId[id] = link;
      targets.push(section);
    });
    if (!targets.length) return;

    function activate(link) {
      links.forEach(function (l) {
        l.classList.toggle('is-active', l === link);
      });
      // Keep the active chip visible on narrow screens.
      if (link && rail.scrollWidth > rail.clientWidth) {
        var left = link.offsetLeft - rail.clientWidth / 2 + link.offsetWidth / 2;
        rail.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }

    if (!('IntersectionObserver' in window)) {
      activate(links[0]);
      return;
    }

    var visible = {};
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          visible[entry.target.id] = entry.isIntersecting ? entry.intersectionRatio : 0;
        });
        var bestId = null;
        var bestRatio = 0;
        targets.forEach(function (section) {
          var ratio = visible[section.id] || 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = section.id;
          }
        });
        if (bestId && byId[bestId]) activate(byId[bestId]);
      },
      { rootMargin: '-70px 0px -55% 0px', threshold: [0, 0.15, 0.4, 0.75, 1] }
    );

    targets.forEach(function (section) {
      observer.observe(section);
    });
  }

  /* -------------------------------------------------- 2. version switching */

  function initVersionSwitch() {
    root.querySelectorAll('.gfm-switch').forEach(function (sw) {
      var buttons = Array.prototype.slice.call(sw.querySelectorAll('button'));
      if (!buttons.length) return;

      // The switch controls every .gfm-ver inside the closest section.
      var scope = sw.closest('.gfm-sec') || root;

      function select(key) {
        buttons.forEach(function (button) {
          button.setAttribute('aria-selected', String(cfgOf(button) === key));
        });
        scope.querySelectorAll('.gfm-ver').forEach(function (el) {
          el.hidden = !el.classList.contains('gfm-ver--' + key);
        });
      }

      buttons.forEach(function (button) {
        if (!button.hasAttribute('type')) button.type = 'button';
        button.addEventListener('click', function () {
          select(cfgOf(button));
        });
      });

      var initial =
        buttons.filter(function (b) {
          return b.getAttribute('aria-selected') === 'true';
        })[0] || buttons[0];
      select(cfgOf(initial));
    });
  }

  /* ------------------------------- 3. live product slots (Section Rendering) */

  function fillSlot(slot, sectionId) {
    var handle = cfgOf(slot);
    if (!handle) return Promise.resolve();

    return fetch(
      ROOT_PATH + 'products/' + encodeURIComponent(handle) + '?section_id=' + sectionId
    )
      .then(function (res) {
        if (!res.ok) throw new Error(String(res.status));
        return res.text();
      })
      .then(function (html) {
        var template = document.createElement('template');
        template.innerHTML = html.trim();

        // The Section Rendering API wraps its output in a #shopify-section-* div.
        // Keeping that div (and the slot around it) inserted two anonymous boxes
        // between the CSS grid and its intended cell — which is what squashed the
        // dark price block in the stat strip. Lift the real children out and
        // replace the slot itself so the rendered element IS the grid item.
        var wrapper = template.content.querySelector('[id^="shopify-section-"]');
        var source = wrapper || template.content;
        var nodes = Array.prototype.filter.call(source.childNodes, function (node) {
          return node.nodeType === 1 || (node.nodeType === 3 && node.textContent.trim());
        });

        if (!nodes.length) {
          slot.classList.remove('gfm-slot--pending');
          return;
        }

        var parent = slot.parentNode;
        nodes.forEach(function (node) {
          parent.insertBefore(node, slot);
        });
        parent.removeChild(slot);
      })
      .catch(function () {
        // Leave the static fallback that the page body already renders.
        slot.classList.remove('gfm-slot--pending');
      });
  }

  function initSlots() {
    root.querySelectorAll('.gfm-slot--configs').forEach(function (slot) {
      fillSlot(slot, 'guide-variant-grid');
    });
    root.querySelectorAll('.gfm-slot--buybar').forEach(function (slot) {
      fillSlot(slot, 'guide-buybar');
    });
    root.querySelectorAll('.gfm-slot--price').forEach(function (slot) {
      fillSlot(slot, 'guide-price');
    });
  }

  /* ---------------------------------------------------- 4. add to cart glue */

  function initCartForms() {
    root.addEventListener('submit', function (event) {
      var form = event.target.closest('form.gfm-add');
      if (!form) return;
      event.preventDefault();

      var button = form.querySelector('button[type="submit"], .gfm-btn');
      var original = button ? button.textContent : '';
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.textContent = t('adding', 'Añadiendo…');
      }

      var data = new FormData(form);
      var line = { id: Number(data.get('id')), quantity: 1 };
      // Preorder variants require their selling plan or the cart answers 422.
      var plan = data.get('selling_plan');
      if (plan) line.selling_plan = Number(plan);

      fetch(ROOT_PATH + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [line] }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error(String(res.status));
          return res.json();
        })
        .then(function () {
          if (button) button.textContent = t('added', 'Añadido ✓');
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          window.setTimeout(function () {
            if (button) {
              button.textContent = original;
              button.removeAttribute('aria-disabled');
            }
          }, 2200);
        })
        .catch(function () {
          // Fall back to a real form post so the customer is never stuck.
          // HTMLFormElement.submit() does not fire the submit event, so this
          // does not re-enter the delegated handler above.
          form.submit();
        });
    });
  }

  /* ------------------------------------------------------------- 5. ask IA */

  function buildAsk(slot) {
    var questions = Array.prototype.slice
      .call(slot.querySelectorAll('.gfm-cfg'))
      .map(function (span) {
        return span.textContent.trim();
      })
      .filter(Boolean);

    var placeholder = t('askPlaceholder', 'Pregunta a esta guía…');

    var section = document.createElement('section');
    section.className = 'gfm-ask';
    section.innerHTML = [
      '<div class="gfm-ask__row">',
      '<div class="gfm-ask__mark" aria-hidden="true">IA</div>',
      '<div class="gfm-ask__field">',
      '<input class="gfm-ask__input" type="text" autocomplete="off"',
      ' aria-label="' + escapeHtml(placeholder) + '"',
      ' placeholder="' + escapeHtml(placeholder) + '">',
      '<span class="gfm-ask__hint" aria-hidden="true">↵ ENTER</span>',
      '</div>',
      '<button class="gfm-ask__send" type="button">' +
        escapeHtml(t('askButton', 'Preguntar')) +
        '</button>',
      '</div>',
      '<div class="gfm-ask__chips"></div>',
      '<div class="gfm-ask__answer" hidden>',
      '<div class="gfm-ask__body">',
      '<div class="gfm-ask__tag">' + escapeHtml(t('answerTag', 'Respuesta IA · citada de esta guía')) + '</div>',
      '<div class="gfm-ask__text"></div>',
      '<div class="gfm-ask__sources"></div>',
      '</div>',
      '<aside class="gfm-ask__aside" hidden></aside>',
      '</div>',
    ].join('');

    var chipBox = section.querySelector('.gfm-ask__chips');
    questions.forEach(function (question) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'gfm-ask__chip';
      chip.textContent = question;
      chipBox.appendChild(chip);
    });
    if (!questions.length) chipBox.remove();

    slot.replaceWith(section);
    return section;
  }

  function initAsk() {
    // A bundle page asks a question per section, not once per page: the reader
    // wonders "does this fit?" while looking at THAT category, not at the top.
    var slots = Array.prototype.slice.call(document.querySelectorAll('.gfm-ask-slot'));
    if (!slots.length) return;
    slots.forEach(initAskSlot);
  }

  function initAskSlot(slot) {
    var endpoint = CONFIG.askUrl;
    if (!endpoint) {
      // No endpoint configured — do not render a dead input.
      slot.remove();
      return;
    }

    var section = buildAsk(slot);
    var input = section.querySelector('.gfm-ask__input');
    var answerBox = section.querySelector('.gfm-ask__answer');
    var textBox = section.querySelector('.gfm-ask__text');
    var sourceBox = section.querySelector('.gfm-ask__sources');
    var aside = section.querySelector('.gfm-ask__aside');
    var sendBtn = section.querySelector('.gfm-ask__send');
    var pending = false;

    /* The guide body carries no prices on purpose — they are rendered live by the
       Section Rendering slots. That means the indexed guide text has none either,
       so "what does it cost?" used to come back as "the passages do not contain
       pricing information". Read what the shop actually rendered on this page and
       hand it to the model as context. Retail only: nothing here knows cost price,
       and this endpoint is public. */
    function priceContext() {
      var buybar = root.querySelector('.gfm-buybar');
      var fromPrice = root.querySelector('.gfm-stats__buy .gfm-price');
      var cards = Array.prototype.slice.call(root.querySelectorAll('.gfm-config'));

      var variants = cards
        .map(function (card) {
          var name = card.querySelector('.gfm-config__name span:last-child');
          var price = card.querySelector('.gfm-config__price');
          if (!name || !price) return null;
          return {
            title: name.textContent.trim(),
            price: price.textContent.replace(/\s+/g, ' ').trim(),
          };
        })
        .filter(Boolean);

      if (!variants.length && !fromPrice) return null;

      return {
        title: buybar
          ? (buybar.querySelector('.gfm-buybar__title') || {}).textContent
          : undefined,
        from: fromPrice ? fromPrice.textContent.trim() : undefined,
        variants: variants.slice(0, 12),
      };
    }

    function showWaiting() {
      answerBox.hidden = false;
      aside.hidden = true;
      sourceBox.textContent = '';
      textBox.innerHTML =
        '<p><span class="gfm-ask__wait"><i></i><i></i><i></i></span> ' +
        escapeHtml(t('thinking', 'Leyendo la guía…')) +
        '</p>';
      answerBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function showError() {
      textBox.innerHTML =
        '<p class="gfm-ask__error">' +
        escapeHtml(
          t('askError', 'La IA no está disponible ahora mismo. Vuelve a intentarlo en un momento.')
        ) +
        '</p>';
    }

    function renderSources(sources) {
      sourceBox.textContent = '';
      (sources || []).slice(0, 4).forEach(function (source) {
        if (!source || !source.title) return;
        var a = document.createElement('a');
        a.className = 'gfm-ask__source';
        a.href = source.url || '#';
        a.textContent = '↗ ' + source.title;
        if (source.url && /^https?:/.test(source.url)) {
          a.target = '_blank';
          a.rel = 'noopener';
        }
        sourceBox.appendChild(a);
      });
    }

    function renderProduct(product) {
      if (!product || !product.title) {
        aside.hidden = true;
        return;
      }
      var url = product.url || (product.handle ? ROOT_PATH + 'products/' + product.handle : '#');
      var html = [
        '<div class="gfm-ask__aside-k">' + escapeHtml(t('youNeed', 'Lo que necesitas')) + '</div>',
        '<a class="gfm-ask__aside-title" href="' + escapeHtml(url) + '">' +
          escapeHtml(product.title) +
          '</a>',
      ];
      if (product.price) {
        html.push('<div class="gfm-ask__aside-price">' + escapeHtml(product.price) + '</div>');
      }
      if (product.variantId) {
        html.push(
          '<form class="gfm-add" method="post" action="' + ROOT_PATH + 'cart/add">',
          '<input type="hidden" name="id" value="' + escapeHtml(product.variantId) + '">',
          '<button class="gfm-btn gfm-btn--primary" type="submit">' +
            escapeHtml(t('addToCart', 'Añadir a la cesta')) +
            '</button>',
          '</form>'
        );
      } else {
        html.push(
          '<a class="gfm-btn gfm-btn--primary" href="' + escapeHtml(url) + '">' +
            escapeHtml(t('viewProduct', 'Ver producto')) +
            '</a>'
        );
      }
      aside.innerHTML = html.join('');
      aside.hidden = false;
    }

    function ask(question) {
      if (pending || !question) return;
      pending = true;
      input.disabled = true;
      if (sendBtn) sendBtn.disabled = true;
      showWaiting();

      var payload = {
        question: question,
        locale: CONFIG.locale || 'es',
        guide: {
          handle: CONFIG.pageHandle || '',
          title: CONFIG.pageTitle || document.title,
          url: window.location.href,
        },
        shop: priceContext() || undefined,
      };

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          if (!res.ok) throw new Error(String(res.status));
          var type = res.headers.get('content-type') || '';
          if (type.indexOf('text/event-stream') !== -1 && res.body) {
            return streamAnswer(res);
          }
          return res.json().then(function (data) {
            textBox.innerHTML = formatAnswer(data.answer) || '';
            renderSources(data.sources);
            renderProduct(data.product);
          });
        })
        .catch(showError)
        .finally(function () {
          pending = false;
          input.disabled = false;
          if (sendBtn) sendBtn.disabled = false;
        });
    }

    /* SSE: `token` events append text, `meta` carries sources + product. */
    function streamAnswer(res) {
      var reader = res.body.getReader();
      var decoder = new TextDecoder();
      var buffer = '';
      var answer = '';
      var started = false;

      function handleEvent(name, data) {
        if (name === 'token' && typeof data.text === 'string') {
          answer += data.text;
          if (!started) {
            started = true;
          }
          textBox.innerHTML = formatAnswer(answer);
        } else if (name === 'meta') {
          renderSources(data.sources);
          renderProduct(data.product);
        } else if (name === 'error') {
          showError();
        }
      }

      function pump() {
        return reader.read().then(function (result) {
          if (result.done) {
            if (!started) showError();
            return;
          }
          buffer += decoder.decode(result.value, { stream: true });
          var blocks = buffer.split('\n\n');
          buffer = blocks.pop() || '';
          blocks.forEach(function (block) {
            var name = 'message';
            var raw = '';
            block.split('\n').forEach(function (line) {
              if (line.indexOf('event:') === 0) name = line.slice(6).trim();
              else if (line.indexOf('data:') === 0) raw += line.slice(5).trim();
            });
            if (!raw) return;
            var data;
            try {
              data = JSON.parse(raw);
            } catch (e) {
              return;
            }
            handleEvent(name, data);
          });
          return pump();
        });
      }

      return pump();
    }

    input.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      ask(input.value.trim());
    });

    if (sendBtn) {
      sendBtn.addEventListener('click', function () {
        var question = input.value.trim();
        if (!question) {
          input.focus();
          return;
        }
        ask(question);
      });
    }

    section.addEventListener('click', function (event) {
      var chip = event.target.closest('.gfm-ask__chip');
      if (!chip) return;
      input.value = chip.textContent;
      ask(chip.textContent);
    });

    // The "Skirmshop IA" badge in the breadcrumb bar focuses the first input.
    var badge = section === root.querySelector('.gfm-ask') ? root.querySelector('.gfm-bar__ia') : null;
    if (badge) {
      badge.addEventListener('click', function () {
        section.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input.focus({ preventScroll: true });
      });
    }
  }

  /* ------------------------------------------------------- 6. 3D camera fit */

  /* The <model-viewer> block lives in the page body with its own loader script
     and data-* hooks. This only corrects the framing: model-viewer's default
     orbit crops the rifle at narrow widths, so pick a radius per breakpoint and
     re-apply it whenever the variant, the model or the viewport changes.
     Previously lived inline in sections/main-page.liquid gated on the VKS page
     handle; it works for any guide that ships the same 3D block. */
  function init3dFit() {
    document.querySelectorAll('[data-vks-3d]').forEach(function (block) {
      var stage = block.querySelector('[data-vks-stage]');
      var tabs = Array.prototype.slice.call(block.querySelectorAll('[data-vks-variant]'));
      if (!stage || !tabs.length) return;
      var resizeTimer;

      function preferredOrbit(index) {
        var theta = index === 0 ? '0deg' : '90deg';
        var radius = window.matchMedia('(max-width: 600px)').matches
          ? '105%'
          : window.matchMedia('(max-width: 900px)').matches
            ? '80%'
            : '60%';
        return theta + ' 80deg ' + radius;
      }

      function applyCameraFit() {
        tabs.forEach(function (tab, index) {
          tab.dataset.orbit = preferredOrbit(index);
        });

        var active =
          tabs.filter(function (tab) {
            return tab.getAttribute('aria-selected') === 'true';
          })[0] || tabs[0];
        var viewer = stage.querySelector('model-viewer');
        if (!viewer || !active) return;

        viewer.removeAttribute('auto-rotate');
        viewer.removeAttribute('auto-rotate-delay');
        viewer.removeAttribute('rotation-per-second');
        viewer.removeAttribute('min-camera-orbit');
        viewer.removeAttribute('max-camera-orbit');
        viewer.setAttribute('field-of-view', 'auto');
        viewer.setAttribute('camera-target', 'auto auto auto');
        viewer.setAttribute('camera-orbit', active.dataset.orbit);
        viewer.cameraOrbit = active.dataset.orbit;
        if (typeof viewer.jumpCameraToGoal === 'function') viewer.jumpCameraToGoal();
      }

      applyCameraFit();

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          window.setTimeout(applyCameraFit, 0);
        });
      });

      if (window.MutationObserver) {
        new MutationObserver(applyCameraFit).observe(stage, { childList: true });
      }

      window.addEventListener('resize', function () {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(applyCameraFit, 120);
      });
    });
  }

  /* ------------------------------------------------------------------ boot */

  function boot() {
    initRail();
    initVersionSwitch();
    initSlots();
    initCartForms();
    initAsk();
    init3dFit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();